import Card from "./Card";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiService from "../services/api";
import { SignupRequest } from "../../shared/types";
import { Input } from "./ui/Input";
import { Button } from "./ui/Button";
import { useAuth } from "../contexts/AuthContext";
import { Notification, Status } from "./ui/Notification";

// Mirrors LBUSERNAME_FORMAT in src/server/lib/lbusername.ts. UX-only pre-check
// before round-trip; server remains the source of truth.
const LBUSERNAME_FORMAT = /^[a-z0-9_-]{2,15}$/;
const LBUSERNAME_FORMAT_MESSAGE =
  "2–15 characters; lowercase letters, numbers, hyphens, underscores only.";

const SignupPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState<
    Required<Pick<SignupRequest, "name" | "email" | "password">> & {
      lbusername: string;
    }
  >({
    name: "",
    email: "",
    password: "",
    lbusername: "",
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<Status>({ type: "idle" });
  const [lbError, setLbError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: "idle" });
    setLbError(null);

    const trimmedLb = formData.lbusername.trim().toLowerCase();

    // Empty is fine — field is optional. Only validate when the user typed
    // something.
    if (trimmedLb.length > 0 && !LBUSERNAME_FORMAT.test(trimmedLb)) {
      setLbError(LBUSERNAME_FORMAT_MESSAGE);
      setLoading(false);
      return;
    }

    // Omit the key entirely when empty — the server skips linking on
    // `undefined` but fails its format check on "".
    const payload: SignupRequest = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      ...(trimmedLb ? { lbusername: trimmedLb } : {}),
    };

    try {
      const response = await apiService.signup(payload);

      if (response.data?.access_token) {
        login(response.data.access_token);
        const redirectPath =
          localStorage.getItem("redirectAfterLogin") || "/dashboard";
        localStorage.removeItem("redirectAfterLogin");
        navigate(redirectPath);
        return;
      }

      // Signup worked but auto-login didn't — usually Supabase requiring email
      // confirmation, so surface the server's message verbatim.
      if (response.message) {
        setStatus({ type: "success", message: response.message });
      } else {
        setStatus({
          type: "error",
          message: "Account created, but no session was issued. Please log in.",
        });
      }
    } catch (err) {
      const raw = err instanceof Error ? err.message : "Signup failed";
      // Route the lbusername-claim conflict to the field-level error so it
      // appears next to the offending input, not the form-level banner.
      if (/already been claimed/i.test(raw)) {
        setLbError(raw);
      } else {
        setStatus({ type: "error", message: raw });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (e.target.name === "lbusername" && lbError) {
      setLbError(null);
    }
  };

  return (
    <div className="min-h-screen bg-letterboxd-bg-primary flex items-start mt-10 justify-center px-4">
      <div className="w-full max-w-md">
        <Card>
          <h2 className="type-h1 text-letterboxd-text-primary mb-6 text-center">
            Create an account
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="name"
                className="block type-body-strong text-letterboxd-text-secondary mb-2"
              >
                Name
              </label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={loading}
                className="w-full"
                placeholder="Enter your name"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block type-body-strong text-letterboxd-text-secondary mb-2"
              >
                Email
              </label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={loading}
                className="w-full"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block type-body-strong text-letterboxd-text-secondary mb-2"
              >
                Password
              </label>
              <Input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                disabled={loading}
                className="w-full"
                placeholder="Enter your password"
              />
            </div>

            <div>
              <label
                htmlFor="lbusername"
                className="block type-body-strong text-letterboxd-text-secondary mb-2"
              >
                Letterboxd.com username
              </label>
              <Input
                type="text"
                id="lbusername"
                name="lbusername"
                value={formData.lbusername}
                onChange={handleInputChange}
                disabled={loading}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                aria-invalid={lbError ? true : undefined}
                aria-describedby={
                  lbError ? "lbusername-help lbusername-error" : "lbusername-help"
                }
                className="w-full"
                placeholder="e.g. davidehrlich"
              />
              <p
                id="lbusername-help"
                className="type-caption text-letterboxd-text-muted mt-1"
              >
                Optional — link your Letterboxd account now or have an admin
                assign it later. We'll fetch your data automatically.
              </p>
              {lbError && (
                <p
                  id="lbusername-error"
                  role="alert"
                  className="type-caption text-letterboxd-error mt-1"
                >
                  {lbError}
                </p>
              )}
            </div>

            <Notification status={status} />

            <Button type="submit" loading={loading} className="w-full mt-6">
              Sign Up
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <p className="text-letterboxd-text-secondary">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-letterboxd-accent hover:text-letterboxd-accent-hover type-strong transition-colors duration-200"
              >
                Login
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SignupPage;
